import { SYSTEM_UCUM } from '@ctw/shared/api/fhir/system-urls';
import type { NormalizedUnitSystem } from '@zeus-health/types';
import type { Quantity } from 'fhir/r4';

interface ImperialConversion {
  unit: string;
  convert: (value: number) => number;
}

/** Imperial conversions, keyed by the UCUM code of the source (metric) unit. */
const IMPERIAL_CONVERSIONS_BY_UCUM_CODE = new Map<string, ImperialConversion>([
  ['Cel', { unit: '°F', convert: (value) => (value * 9) / 5 + 32 }],
  ['kg', { unit: 'lb', convert: (value) => value * 2.20462 }],
  ['g', { unit: 'oz', convert: (value) => value * 0.035274 }],
  ['cm', { unit: 'in', convert: (value) => value / 2.54 }],
]);

/** Rounds to `decimalPlaces`, breaking ties away from zero rather than toward +Infinity. */
function roundHalfAwayFromZero(value: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  const scaled = value * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return rounded / factor;
}

/** Converts a FHIR Quantity to imperial using its UCUM code, or `unit` when uncoded. */
export function convertQuantityToImperial(
  quantity: Quantity | undefined,
  unitSystem: NormalizedUnitSystem | undefined,
): { value: string; unit: string } | undefined {
  if (unitSystem !== 'imperial' || !quantity || quantity.value === undefined) {
    return undefined;
  }

  const systemIsAbsent = quantity.system === undefined || quantity.system === null;
  const codeIsAbsent = quantity.code === undefined || quantity.code === null;

  let sourceCode: string | undefined;
  if (quantity.system === SYSTEM_UCUM && !codeIsAbsent) {
    sourceCode = quantity.code;
  } else if (systemIsAbsent && codeIsAbsent) {
    sourceCode = quantity.unit;
  } else {
    return undefined;
  }

  const conversion = sourceCode ? IMPERIAL_CONVERSIONS_BY_UCUM_CODE.get(sourceCode) : undefined;
  if (!conversion) {
    return undefined;
  }
  const roundedValue = roundHalfAwayFromZero(conversion.convert(quantity.value), 1);
  return { value: roundedValue.toFixed(1), unit: conversion.unit };
}
